import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FaTicketAlt, FaMapMarkerAlt, FaExclamationTriangle, FaTimes, FaCheckCircle, FaClock, FaTimesCircle, FaGift } from 'react-icons/fa';
import { TICKET_RAFFLE_PRESET_SLOTS, TICKET_RAFFLE_PRESET_TOTAL_SLOTS } from '../constants/ticketRafflePreset';

function easeOutCubic(t) {
  const x = Math.min(1, Math.max(0, t));
  return 1 - (1 - x) ** 3;
}

/**
 * Çekiliş sırasında isimleri rastgeleymiş gibi hızlıca döndürür, süre ilerledikçe yavaşlar;
 * süre dolunca hep `finalName` gösterilir (sonuç panel kaydıdır, burada seçilmez).
 */
function spinTickerDisplay(namePool, finalName, durationMs, setDisplay) {
  return new Promise((resolve) => {
    const raw = namePool.length > 0 ? namePool : [finalName];
    const pool = [...new Set(raw.map((x) => String(x)))];
    if (pool.length === 0) pool.push(String(finalName));
    const start = performance.now();
    let lastShown = '';

    const step = () => {
      const now = performance.now();
      const rawT = (now - start) / durationMs;

      if (rawT >= 1) {
        setDisplay(finalName);
        resolve();
        return;
      }

      let pick = pool[Math.floor(Math.random() * pool.length)];
      if (pool.length > 2 && pick === lastShown) {
        const idx = pool.indexOf(pick);
        const offset = 1 + Math.floor(Math.random() * (pool.length - 1));
        pick = pool[(idx + offset) % pool.length];
      }
      lastShown = pick;
      setDisplay(pick);

      const eased = easeOutCubic(rawT);
      const minGap = 24;
      const maxGap = 340;
      const gap = minGap + (maxGap - minGap) * eased;
      const jitter = (Math.random() - 0.5) * 20 * (1 - eased * 0.9);
      setTimeout(step, Math.max(20, gap + jitter));
    };

    step();
  });
}

const raffleAuthHeader = () => {
  const t = localStorage.getItem('adminToken') || localStorage.getItem('userToken');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const formatTryTr = (n) => `${new Intl.NumberFormat('tr-TR').format(Number(n) || 0)} ₺`;

const getEventType = (event) => event?.eventType || event?.event_type || 'deposit';

function decodeJwtPayload(token) {
  if (!token || typeof token !== 'string') return null;
  const parts = token.split('.');
  if (parts.length < 2) return null;
  try {
    let b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    while (b64.length % 4) b64 += '=';
    const jsonPayload = JSON.parse(
      decodeURIComponent([...atob(b64)].map((c) => `%${(`00${c.charCodeAt(0).toString(16)}`).slice(-2)}`).join(''))
    );
    return jsonPayload;
  } catch {
    return null;
  }
}

/** İlk yüklemede doğru görünüm (çekiliş butonu) — sonra storage/JWT güncellenir */
function computeStoredRaffleAdminFlag() {
  if (typeof localStorage === 'undefined') return false;
  if (localStorage.getItem('adminToken')) return true;
  try {
    const u = JSON.parse(localStorage.getItem('userData') || '{}');
    if (u.role === 'admin') return true;
  } catch {
    /* ignore */
  }
  const p = decodeJwtPayload(localStorage.getItem('userToken'));
  return Boolean(p && (p.type === 'admin' || (p.type === 'user' && p.role === 'admin')));
}

const TicketsPage = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [sponsors, setSponsors] = useState([]);
  const [formData, setFormData] = useState({
    sponsorId: '',
    siteUsername: '',
    depositAmount: '',
    proofImage: null
  });
  const [selectedSponsorMultiplier, setSelectedSponsorMultiplier] = useState(1);
  const [loading, setLoading] = useState(false);
  const [isUser, setIsUser] = useState(false);
  const [activeTab, setActiveTab] = useState('events'); // 'events' | 'my-tickets'
  const [myTickets, setMyTickets] = useState([]);
  const [showTicketForm, setShowTicketForm] = useState(false);
  const [formEventId, setFormEventId] = useState(null);
  const [countdowns, setCountdowns] = useState({});
  const [userIsRaffleAdmin, setUserIsRaffleAdmin] = useState(computeStoredRaffleAdminFlag);
  const [raffleMetaByEvent, setRaffleMetaByEvent] = useState({});
  const [raffleBusy, setRaffleBusy] = useState(false);
  const [raffleOverlay, setRaffleOverlay] = useState(null);
  const [guestAdminBrowsing, setGuestAdminBrowsing] = useState(false);

  // Etkinlik kartlarını sayıya göre merkezlemek için responsive column ayarı
  const eventsGridCols =
    events.length === 1
      ? 'grid-cols-1'
      : events.length === 2
        ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-2'
        : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3';

  const checkUser = useCallback(() => {
    const token = localStorage.getItem('userToken');
    const adminTok = localStorage.getItem('adminToken');
    setIsUser(!!token);
    setGuestAdminBrowsing(!!adminTok && !token);
  }, []);

  const fetchEvents = useCallback(async () => {
    try {
      const response = await axios.get('/api/tickets/events');
      setEvents(response.data);
      if (response.data.length > 0 && !selectedEvent) {
        setSelectedEvent(response.data[0].id);
      }
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  }, [selectedEvent]);

  const fetchSponsors = useCallback(async () => {
    if (!selectedEvent) return;
    
    try {
      const response = await axios.get('/api/sponsors');
      const event = events.find(e => e.id === selectedEvent);
      if (!event) return;
      const validSponsorIds = JSON.parse(event.validSponsorIds || event.valid_sponsor_ids || '[]');
      const validSponsors = response.data.filter(sponsor => 
        validSponsorIds.includes(sponsor.id)
      );
      setSponsors(validSponsors);
    } catch (error) {
      console.error('Error fetching sponsors:', error);
    }
  }, [selectedEvent, events]);

  useEffect(() => {
    checkUser();
    fetchEvents();
    const onTok = () => checkUser();
    window.addEventListener('adminTokenChanged', onTok);
    return () => window.removeEventListener('adminTokenChanged', onTok);
  }, [checkUser, fetchEvents]);

  useEffect(() => {
    if (selectedEvent) {
      fetchSponsors();
    }
  }, [selectedEvent, fetchSponsors]);

  // Tek sponsor varsa kullanıcı seçmeden otomatik seç.
  useEffect(() => {
    if (!showTicketForm) return;
    if (!sponsors || sponsors.length !== 1) return;
    const onlySponsor = sponsors[0];
    const multiplier = onlySponsor?.ticket_multiplier || onlySponsor?.ticketMultiplier || 1;
    setSelectedSponsorMultiplier(multiplier);
    setFormData(prev => ({ ...prev, sponsorId: onlySponsor.id.toString() }));
  }, [showTicketForm, sponsors]);

  const fetchMyTickets = useCallback(async () => {
    if (!isUser) return;
    try {
      const token = localStorage.getItem('userToken');
      const response = await axios.get('/api/tickets/my-tickets', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyTickets(response.data);
    } catch (error) {
      console.error('Error fetching my tickets:', error);
    }
  }, [isUser]);

  useEffect(() => {
    if (isUser && activeTab === 'my-tickets') {
      fetchMyTickets();
    }
  }, [isUser, activeTab, fetchMyTickets]);

  useEffect(() => {
    const checkRa = () => setUserIsRaffleAdmin(computeStoredRaffleAdminFlag());
    checkRa();
    window.addEventListener('storage', checkRa);
    window.addEventListener('userTokenChanged', checkRa);
    window.addEventListener('adminTokenChanged', checkRa);
    return () => {
      window.removeEventListener('storage', checkRa);
      window.removeEventListener('userTokenChanged', checkRa);
      window.removeEventListener('adminTokenChanged', checkRa);
    };
  }, []);

  const fetchRaffleMeta = useCallback(async () => {
    if (!userIsRaffleAdmin) return;
    const hasAuth = !!(typeof localStorage !== 'undefined' && (localStorage.getItem('adminToken') || localStorage.getItem('userToken')));
    if (!hasAuth) return;
    try {
      const { data } = await axios.get('/api/tickets/admin/raffle/public-events-meta', {
        headers: raffleAuthHeader()
      });
      const map = {};
      (Array.isArray(data) ? data : []).forEach((row) => {
        map[row.eventId] = row;
      });
      setRaffleMetaByEvent(map);
    } catch (e) {
      console.warn('Çekiliş özeti alınamadı', e);
      setRaffleMetaByEvent({});
    }
  }, [userIsRaffleAdmin]);

  useEffect(() => {
    fetchRaffleMeta();
  }, [events, fetchRaffleMeta]);

  // Countdown timer için useEffect
  useEffect(() => {
    const updateCountdowns = () => {
      const newCountdowns = {};
      events.forEach(event => {
        const endDate = event.endDate || event.end_date;
        if (endDate) {
          const now = new Date();
          const end = new Date(endDate);
          const diff = end - now;
          
          if (diff <= 0) {
            newCountdowns[event.id] = { days: 0, hours: 0, minutes: 0, seconds: 0 };
          } else {
            const days = Math.floor(diff / (1000 * 60 * 60 * 24));
            const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
            const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
            const seconds = Math.floor((diff % (1000 * 60)) / 1000);
            newCountdowns[event.id] = { days, hours, minutes, seconds };
          }
        }
      });
      setCountdowns(newCountdowns);
    };
    
    updateCountdowns();
    const interval = setInterval(updateCountdowns, 1000);
    
    return () => clearInterval(interval);
  }, [events]);

  const handleSponsorChange = (sponsorId) => {
    const sponsor = sponsors.find(s => s.id === parseInt(sponsorId));
    const multiplier = sponsor?.ticket_multiplier || sponsor?.ticketMultiplier || 1;
    setSelectedSponsorMultiplier(multiplier);
    setFormData({ ...formData, sponsorId });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isUser) {
      navigate('/login');
      return;
    }

    const event = events.find((ev) => ev.id === formEventId);
    const isReferral = event && getEventType(event) === 'referral';

    if (isReferral) {
      if (!formData.sponsorId || !formData.siteUsername) {
        alert('Site ve arkadaş kullanıcı adı gereklidir');
        return;
      }
    } else if (!formData.sponsorId || !formData.siteUsername || !formData.depositAmount || !formData.proofImage) {
      alert('Tüm alanlar gereklidir');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('userToken');

      if (isReferral) {
        await axios.post(
          '/api/tickets/referral-request',
          {
            ticketEventId: formEventId || selectedEvent,
            sponsorId: formData.sponsorId,
            friendUsername: formData.siteUsername
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        alert('Arkadaş davet talebiniz alındı. Yönetici onayından sonra biletleriniz tanımlanacaktır.');
      } else {
        const formDataToSend = new FormData();
        formDataToSend.append('ticketEventId', formEventId || selectedEvent);
        formDataToSend.append('sponsorId', formData.sponsorId);
        formDataToSend.append('siteUsername', formData.siteUsername);
        formDataToSend.append('depositAmount', formData.depositAmount);
        formDataToSend.append('proofImage', formData.proofImage);

        await axios.post('/api/tickets/request', formDataToSend, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });

        alert('Bilet talebiniz alındı. Yönetici onayından sonra sonuçlanacaktır.');
      }
      setFormData({
        sponsorId: '',
        siteUsername: '',
        depositAmount: '',
        proofImage: null
      });
      setShowTicketForm(false);
      fetchMyTickets();
    } catch (error) {
      console.error('Error requesting ticket:', error);
      alert(error.response?.data?.error || 'Bilet talebi oluşturulurken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const openTicketForm = (eventId) => {
    setFormEventId(eventId);
    setSelectedEvent(eventId);
    setShowTicketForm(true);
  };

  const handleAnimatedRaffle = async (ticketEventId, eventTitleLabel) => {
    if (!userIsRaffleAdmin || raffleBusy) return;
    if (
      !window.confirm(
        `“${eventTitleLabel}” — ${TICKET_RAFFLE_PRESET_TOTAL_SLOTS} ödüllük çekiliş animasyonunu tek seferde başlatmak üzeresiniz.\nKatılımcılar arasında özellikle 20 bileti olanların isimleri ekranda karışarak döner ve her ödül rastgele seçilmiş görünümüyle açıklanır.\nSagu Coin yüklenmez; çekiliş kaydı kapanır — ödemeyi nakit olarak siz yaparsınız.`
      )
    ) {
      return;
    }
    setRaffleBusy(true);
    try {
      const headers = raffleAuthHeader();
      const stateRes = await axios.get(`/api/tickets/admin/raffle/${ticketEventId}/state`, { headers });
      const eligibleUsersRaw = Array.isArray(stateRes.data?.eligibleUsers) ? stateRes.data.eligibleUsers : [];

      const allTicketNumbers = [];
      for (const u of eligibleUsersRaw) {
        const arr = Array.isArray(u.ticketNumbers) ? u.ticketNumbers : [];
        for (const n of arr) {
          const nn = parseInt(n, 10);
          if (!Number.isNaN(nn)) allTicketNumbers.push(nn);
        }
      }
      const uniqueTickets = [...new Set(allTicketNumbers)].sort((a, b) => a - b);
      const ticketPool = uniqueTickets.map((n) => `Bilet #${n}`);
      const spinPoolCaption = `Animasyonda satılmış tüm bilet numaraları döner (${uniqueTickets.length} bilet).`;

      const pending = Array.isArray(stateRes.data?.pendingAwards) ? stateRes.data.pendingAwards : [];
      if (pending.length !== TICKET_RAFFLE_PRESET_TOTAL_SLOTS) {
        alert(`Tam ${TICKET_RAFFLE_PRESET_TOTAL_SLOTS} kazanan kaydı olmalı (100.000 ₺ yapısı). Şu an: ${pending.length}.`);
        return;
      }
      if (ticketPool.length === 0) {
        alert('Bu etkinlikte satılmış/onaylı bilet bulunamadı.');
        return;
      }

      const winners = pending.map((pa) => {
        const nm = pa.username && String(pa.username).trim();
        return {
          username: nm || `Üye #${pa.userId}`,
          prizeAmountTry: pa.prizeAmountTry ?? pa.prizeCoins,
          winningTicketNumber: pa.winningTicketNumber ?? pa.winning_ticket_number,
          userId: pa.userId
        };
      });

      const userLineWin = (w) => {
        const eu = eligibleUsersRaw.find((x) => Number(x.userId) === Number(w.userId));
        const base = w.username || '—';
        const cnt = eu ? Number(eu.totalTicketCount) || 0 : null;
        const tno = w?.winningTicketNumber ? ` · Bilet #${w.winningTicketNumber}` : '';
        if (cnt != null) return `${base} · ${cnt} bilet${tno}`;
        return `${base}${tno}`;
      };

      const ticketTarget = (w) => `Bilet #${w?.winningTicketNumber ?? '—'}`;

      const leaderboard = TICKET_RAFFLE_PRESET_SLOTS.map((slot) => ({
        prizeLabel: slot.subtitle,
        amountTry: slot.amountTry,
        nameLine: null
      }));

      setRaffleOverlay({
        phase: 'spin',
        eventTitle: eventTitleLabel || 'Etkinlik',
        spinPoolCaption,
        leaderboard,
        currentIndex: -1,
        displayName: '…',
        revealedWinnerLine: null,
        winners,
        totals: winners.length,
        subtitle: `${TICKET_RAFFLE_PRESET_TOTAL_SLOTS} ödül sırayla`,
        revealedTryForSlot: null
      });

      for (let i = 0; i < winners.length; i++) {
        const prizeLabel = TICKET_RAFFLE_PRESET_SLOTS[i]?.subtitle ?? `Ödül ${i + 1}`;
        const targetTicket = ticketTarget(winners[i]);
        const winnerLine = userLineWin(winners[i]);
        const prizeTry = winners[i]?.prizeAmountTry ?? winners[i]?.prizeCoins;
        setRaffleOverlay((prev) => ({
          ...prev,
          phase: 'spin',
          currentIndex: i,
          displayName: '…',
          subtitle: `${prizeLabel} · ${i + 1}/${TICKET_RAFFLE_PRESET_TOTAL_SLOTS} · Rastgele seçiliyor`,
          revealedTryForSlot: null
        }));
        await spinTickerDisplay(ticketPool, targetTicket, 2600 + i * 260, (n) =>
          setRaffleOverlay((prev) => (prev ? { ...prev, phase: 'spin', displayName: n, currentIndex: i } : prev))
        );
        await new Promise((r) => setTimeout(r, 360));
        if (prizeTry != null) {
          setRaffleOverlay((prev) =>
            prev
              ? {
                  ...prev,
                  displayName: targetTicket,
                  revealedTryForSlot: prizeTry,
                  revealedWinnerLine: winnerLine
                }
              : prev
          );
        }
        await new Promise((r) => setTimeout(r, 480));
        setRaffleOverlay((prev) => {
          if (!prev?.leaderboard) return prev;
          const nextLb = prev.leaderboard.map((row, j) =>
            j === i ? { ...row, nameLine: winnerLine } : row
          );
          return { ...prev, leaderboard: nextLb };
        });
      }

      setRaffleOverlay((prev) =>
        prev
          ? { ...prev, phase: 'paying', displayName: 'Kayıtlar kapatılıyor…', revealedTryForSlot: null }
          : prev
      );
      const payRes = await axios.post(`/api/tickets/admin/raffle/${ticketEventId}/run`, {}, { headers });

      const msg =
        payRes.data?.message ||
        `Tamam — ${payRes.data?.completedAwardRows ?? winners.length} çekiliş kaydı tamamlandı (Sagu Coin yüklemesi yapılmadı).`;
      alert(msg);
      fetchRaffleMeta();
      setRaffleOverlay((prev) =>
        prev ? { ...prev, phase: 'done', displayName: 'Çekiliş tamamlandı' } : prev
      );
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.error || err.message || 'Çekiliş tamamlanamadı.');
    } finally {
      setRaffleBusy(false);
    }
  };


  if (!isUser && !guestAdminBrowsing) {
    return (
      <div className="min-h-screen bg-gradient-dark flex items-center justify-center py-12 px-4">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-gold mb-4">Bilet Talep Etmek İçin Giriş Yapmalısınız</h2>
          <button
            onClick={() => navigate('/login')}
            className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-bold py-3 px-6 rounded-lg transition-all duration-300"
          >
            Giriş Yap
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-dark py-12 px-4">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-4xl font-bold text-center text-gold mb-8">Bilet Etkinlikleri</h1>

        {guestAdminBrowsing && (
          <p className="text-center text-amber-200/90 mb-8 text-sm md:text-base max-w-2xl mx-auto px-4">
            Panele admin olarak bağlandınız; bu görünüm çekiliş başlatmak içindir. Bilet başvurmak için site kullanıcı
            hesabıyla çıkış yapıp tekrar giriş yapın.
          </p>
        )}

        {/* Tabs */}
        {isUser && (
          <div className="flex gap-4 mb-8 justify-center">
            <button
              onClick={() => setActiveTab('events')}
              className={`px-6 py-3 rounded-lg font-semibold transition-all ${
                activeTab === 'events'
                  ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white'
                  : 'bg-navy-700 text-white/60 hover:text-white'
              }`}
            >
              Etkinlikler
            </button>
            <button
              onClick={() => setActiveTab('my-tickets')}
              className={`px-6 py-3 rounded-lg font-semibold transition-all ${
                activeTab === 'my-tickets'
                  ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white'
                  : 'bg-navy-700 text-white/60 hover:text-white'
              }`}
            >
              Biletlerim
            </button>
          </div>
        )}

        {/* My Tickets Tab */}
        {isUser && activeTab === 'my-tickets' && (
          <div className="space-y-4">
            {myTickets.length === 0 ? (
              <div className="text-center text-white/60 py-12">
                <p className="text-xl">Henüz bilet talebiniz bulunmamaktadır.</p>
              </div>
            ) : (
              myTickets.map((ticket) => {
                const event =
                  ticket.ticketEvent ||
                  (ticket.event_title
                    ? {
                        title: ticket.event_title,
                        eventType: ticket.event_type,
                        ticketPrice: ticket.ticket_price
                      }
                    : {});
                const isReferralTicket =
                  getEventType(event) === 'referral' || ticket.event_type === 'referral';
                const ticketNumbers = ticket.ticketNumbersArray || [];
                const status = ticket.status;
                
                return (
                  <div
                    key={ticket.id}
                    className="bg-gradient-to-br from-navy-800/50 to-navy-950/50 rounded-xl p-6 border-2 border-gold/30"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-xl font-bold text-white mb-2">{event.title || 'Etkinlik'}</h3>
                        <div className="text-white/60 text-sm">
                          {ticket.createdAt || ticket.created_at 
                            ? new Date(ticket.createdAt || ticket.created_at).toLocaleString('tr-TR')
                            : '-'}
                        </div>
                      </div>
                      <div>
                        {status === 'approved' && (
                          <span className="bg-green-500 text-white text-xs px-3 py-1 rounded-full flex items-center gap-1">
                            <FaCheckCircle />
                            Onaylandı
                          </span>
                        )}
                        {status === 'pending' && (
                          <span className="bg-yellow-500 text-white text-xs px-3 py-1 rounded-full flex items-center gap-1">
                            <FaClock />
                            Bekliyor
                          </span>
                        )}
                        {status === 'rejected' && (
                          <span className="bg-red-500 text-white text-xs px-3 py-1 rounded-full flex items-center gap-1">
                            <FaTimesCircle />
                            Reddedildi
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-4">
                      {!isReferralTicket && (
                      <div>
                        <div className="text-white/60 text-sm mb-1">Yatırım Tutarı</div>
                        <div className="text-white font-semibold">
                          {ticket.depositAmount || ticket.deposit_amount || 0}₺
                        </div>
                      </div>
                      )}
                      <div>
                        <div className="text-white/60 text-sm mb-1">Bilet Sayısı</div>
                        <div className="text-white font-semibold">
                          {ticket.ticketCount || ticket.ticket_count || 0} adet
                        </div>
                      </div>
                      <div>
                        <div className="text-white/60 text-sm mb-1">{isReferralTicket ? 'Arkadaş Kullanıcı Adı' : 'Site Kullanıcı Adı'}</div>
                        <div className="text-white font-semibold">
                          {ticket.siteUsername || ticket.site_username || '-'}
                        </div>
                      </div>
                      <div>
                        <div className="text-white/60 text-sm mb-1">Telegram</div>
                        <div className="text-white font-semibold break-all">
                          {(() => {
                            const u = ticket.telegramUsername || ticket.telegram_username;
                            if (!u) return '—';
                            const handle = String(u).replace(/^@/, '');
                            return (
                              <a
                                href={`https://t.me/${handle}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-navy-300 hover:text-navy-200 underline-offset-2 hover:underline"
                              >
                                @{handle}
                              </a>
                            );
                          })()}
                        </div>
                      </div>
                      <div>
                        <div className="text-white/60 text-sm mb-1">Sponsor</div>
                        <div className="text-white font-semibold">
                          {ticket.sponsorName || ticket.sponsor_name || '-'}
                        </div>
                      </div>
                    </div>

                    {ticketNumbers.length > 0 && (
                      <div className="bg-navy-800/50 rounded-lg p-4 border border-gold/20">
                        <div className="text-white/60 text-sm mb-2">Bilet Numaraları:</div>
                        <div className="flex flex-wrap gap-2">
                          {ticketNumbers.map((num, idx) => (
                            <span
                              key={idx}
                              className="bg-gold/20 text-gold px-3 py-1 rounded-full text-sm font-semibold"
                            >
                              {num}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* Events Tab */}
        {activeTab === 'events' && (
          <>
            {events.length === 0 ? (
              <div className="text-center text-white/60 py-12">
                <p className="text-xl">Şu anda aktif etkinlik bulunmamaktadır.</p>
              </div>
            ) : (
              <div className={`grid ${eventsGridCols} gap-6 justify-items-center justify-center`}>
                {events.map((event) => {
                  const isReferralEvent = getEventType(event) === 'referral';
                  const ticketsPerFriend =
                    event.referralTicketsPerFriend || event.referral_tickets_per_friend || 1;
                  const isActive = event.isActive === true || event.is_active === 1;
                  const endDate = event.endDate || event.end_date;
                  const isExpired = endDate && new Date(endDate) < new Date();
                  const timeRemaining = countdowns[event.id] || { days: 0, hours: 0, minutes: 0, seconds: 0 };
                  
                  const imageUrl = event.imageUrl || event.image_url;
                  const getImageUrl = (url) => {
                    if (!url) return null;
                    if (url.startsWith('http://') || url.startsWith('https://')) {
                      return url;
                    }
                    if (url.startsWith('/uploads/')) {
                      return url;
                    }
                    return url.startsWith('/') ? url : `/uploads/${url}`;
                  };
                  
                  return (
                    <div
                      key={event.id}
                      className="w-full max-w-sm bg-gradient-to-br from-navy-800/50 to-navy-950/50 rounded-xl p-4 border-2 border-gold/30 shadow-2xl overflow-hidden"
                    >
                      {/* Görsel */}
                      {imageUrl && (
                        <div
                          className="mb-4 rounded-lg overflow-hidden bg-navy-900/30 border border-gold/10 w-full"
                          style={{ aspectRatio: '1 / 1', maxHeight: '260px' }}
                        >
                          <img
                            src={getImageUrl(imageUrl)}
                            alt={event.title}
                            className="w-full h-full object-contain"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        </div>
                      )}
                      
                      {/* Başlık */}
                      <h2 className="text-lg font-bold text-white mb-3 line-clamp-2">{event.title}</h2>
                      
                      {/* Bilet Fiyatı */}
                      <div className="bg-blue-900/95 rounded-lg p-3 mb-4 border border-blue-600/60">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-white/60 text-xs mb-1">{isReferralEvent ? 'HER ARKADAŞ İÇİN' : 'BİLET FİYATI'}</div>
                            <div className="text-white text-xl font-bold">
                              {isReferralEvent ? `${ticketsPerFriend} bilet` : `${event.ticketPrice || event.ticket_price}₺`}
                            </div>
                          </div>
                          <div className="bg-blue-600 rounded-lg p-2">
                            <FaTicketAlt className="w-6 h-6 text-white" />
                          </div>
                        </div>
                      </div>

                      {/* Bitmesine Kalan Süre */}
                      {endDate && !isExpired && (
                        <div className="mb-4">
                          <div className="text-white/60 text-xs mb-2">BİTMESİNE KALAN SÜRE</div>
                          <div className="flex gap-2 md:gap-4">
                            <div className="bg-navy-800/70 px-3 py-2 rounded-lg border border-gold/30 flex-1 text-center">
                              <div className="text-lg md:text-xl font-bold text-gold">{timeRemaining.days}</div>
                              <div className="text-xs text-white/80">GÜN</div>
                            </div>
                            <div className="bg-navy-800/70 px-3 py-2 rounded-lg border border-gold/30 flex-1 text-center">
                              <div className="text-lg md:text-xl font-bold text-gold">{timeRemaining.hours}</div>
                              <div className="text-xs text-white/80">SAAT</div>
                            </div>
                            <div className="bg-navy-800/70 px-3 py-2 rounded-lg border border-gold/30 flex-1 text-center">
                              <div className="text-lg md:text-xl font-bold text-gold">{timeRemaining.minutes}</div>
                              <div className="text-xs text-white/80">DAKİKA</div>
                            </div>
                            <div className="bg-navy-800/70 px-3 py-2 rounded-lg border border-gold/30 flex-1 text-center">
                              <div className="text-lg md:text-xl font-bold text-gold">{timeRemaining.seconds}</div>
                              <div className="text-xs text-white/80">SANİYE</div>
                            </div>
                          </div>
                        </div>
                      )}

                      {!isReferralEvent && userIsRaffleAdmin && (() => {
                        const rm = raffleMetaByEvent[event.id];
                        const needSlots = rm?.requiredPresetAwardCount ?? TICKET_RAFFLE_PRESET_TOTAL_SLOTS;
                        const pendingOk = Number(rm?.pendingAwardCount ?? rm?.prizeSlotCount ?? 0) === needSlots;
                        const lackMeta = !rm || !pendingOk;
                        const raffleDisabled = raffleBusy || lackMeta;
                        return (
                          <button
                            type="button"
                            onClick={() => handleAnimatedRaffle(event.id, event.title)}
                            disabled={!!raffleDisabled}
                            title={
                              lackMeta
                                ? !rm
                                  ? 'Çekiliş özeti yüklenemedi — sayfayı yenileyin veya tekrar giriş yapın.'
                                  : `Panelde tam ${needSlots} kazanan kaydı (100.000 ₺ yapısı) bekleniyor. Bekleyen: ${rm.pendingAwardCount ?? '—'}, 20+ biletli: ${rm.eligibleWithTwentyPlusTickets ?? '—'}`
                                : `${needSlots} ödül sırayla açılacak`
                            }
                            className={`w-full mb-2 bg-gradient-to-r from-amber-600 to-yellow-500 hover:from-amber-500 hover:to-yellow-400 disabled:opacity-45 disabled:cursor-not-allowed text-navy-950 font-bold py-2.5 md:py-3 rounded-lg transition-all duration-300 flex items-center justify-center gap-2 text-sm md:text-base border border-yellow-600/70 shadow-lg`}
                          >
                            <FaGift />
                            <span>{raffleBusy ? 'İşlem sürüyor…' : 'Çekiliş yap (yönetici)'}</span>
                          </button>
                        );
                      })()}

                      <button
                        onClick={() => openTicketForm(event.id)}
                        disabled={!isUser || !isActive || isExpired}
                        className="w-full bg-gradient-to-r from-green-500 to-blue-500 hover:from-green-600 hover:to-blue-600 text-white font-bold py-2.5 md:py-3 rounded-lg transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed text-sm md:text-base"
                      >
                        <FaTicketAlt />
                        <span>{isReferralEvent ? 'Arkadaş Davet Et' : 'Bilet Al'}</span>
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      {raffleOverlay && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 backdrop-blur-md p-3 sm:p-6 overflow-y-auto">
          <div className="w-full max-w-5xl flex flex-col gap-4">
            <div className="text-center space-y-2">
              <p className="text-gold/90 tracking-wide uppercase text-xs font-semibold">
                Bilet Çekilişi · {raffleOverlay.eventTitle}
              </p>
              <div className="flex justify-center">
                <button
                  type="button"
                  onClick={() => setRaffleOverlay(null)}
                  className="px-4 py-2 rounded-lg border border-white/20 text-white/85 hover:text-white hover:border-white/40 bg-white/5 text-sm"
                >
                  Kapat
                </button>
              </div>
            </div>

            <div className="flex flex-col lg:flex-row gap-4 lg:gap-6 items-stretch">
              <aside className="w-full lg:w-80 shrink-0 rounded-xl border border-amber-500/35 bg-navy-950/90 p-3 sm:p-4 flex flex-col max-h-[40vh] sm:max-h-[45vh] lg:max-h-[72vh]">
                <h3 className="text-amber-300/95 text-xs font-bold uppercase tracking-wider mb-3 shrink-0">
                  Kazananlar
                </h3>
                <div className="space-y-2 overflow-y-auto flex-1 min-h-0 pr-1">
                  {(raffleOverlay.leaderboard || []).map((row, idx) => {
                    const active = raffleOverlay.currentIndex === idx;
                    return (
                      <div
                        key={idx}
                        className={`rounded-lg border px-2.5 py-2 text-left transition-colors ${
                          active
                            ? 'border-amber-400/80 bg-amber-950/40 shadow-[0_0_12px_rgba(251,191,36,0.2)]'
                            : 'border-gold/15 bg-navy-900/50'
                        }`}
                      >
                        <div className="text-amber-200 font-bold text-sm">{formatTryTr(row.amountTry)}</div>
                        <div className="text-white/45 text-[10px] sm:text-xs leading-tight mb-1">{row.prizeLabel}</div>
                        <div className="text-white text-xs sm:text-sm font-semibold leading-snug break-words">
                          {row.nameLine ?? <span className="text-white/35">Bekleniyor…</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </aside>

              <div className="flex-1 min-w-0">
                <div className="relative min-h-[10rem] sm:min-h-[12rem] flex flex-col items-center justify-center rounded-2xl border-2 border-amber-500/55 bg-gradient-to-b from-navy-900/90 to-navy-950/95 shadow-[0_0_40px_rgba(251,191,36,0.28)] px-4 py-6 overflow-hidden text-center">
                  {raffleOverlay.phase === 'spin' && raffleOverlay.revealedTryForSlot == null ? (
                    <>
                      <div
                        aria-hidden
                        className="pointer-events-none absolute inset-0 rounded-2xl opacity-70 bg-[linear-gradient(transparent_0%,rgba(251,191,36,0.06)_40%,rgba(251,191,36,0.14)_50%,rgba(251,191,36,0.06)_60%,transparent_100%)] motion-safe:animate-raffle-shine"
                      />
                      <div
                        aria-hidden
                        className="pointer-events-none absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-gradient-to-r from-transparent via-amber-400/45 to-transparent"
                      />
                    </>
                  ) : null}
                  <span className="text-white/55 text-sm mb-2 relative z-[1]">{raffleOverlay.subtitle}</span>
                  <span
                    className={`relative z-[1] text-2xl sm:text-4xl md:text-5xl font-extrabold break-words leading-tight drop-shadow-lg transition-[transform,filter] duration-100 motion-reduce:transition-none ${
                      raffleOverlay.phase === 'spin' && raffleOverlay.revealedTryForSlot == null
                        ? 'text-white scale-[1.02] brightness-110 motion-safe:animate-pulse'
                        : 'text-white'
                    }`}
                  >
                    {raffleOverlay.displayName}
                  </span>
                  {raffleOverlay.revealedTryForSlot != null ? (
                    <div className="mt-5 relative z-[1] space-y-2">
                      {raffleOverlay.revealedWinnerLine ? (
                        <div className="text-white/90 text-sm sm:text-base font-semibold">
                          {raffleOverlay.revealedWinnerLine}
                        </div>
                      ) : null}
                      <div className="text-lg sm:text-xl font-bold text-amber-300">
                        Nakit ödül: {formatTryTr(raffleOverlay.revealedTryForSlot)}
                      </div>
                    </div>
                  ) : raffleOverlay.phase === 'paying' ? (
                    <span className="mt-5 text-lg sm:text-xl font-semibold text-navy-200 animate-pulse relative z-[1]">
                      Bekleyin… Kayıtlar tamamlanıyor
                    </span>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Ticket Form Modal */}
      {showTicketForm && formEventId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
          <div className="bg-gradient-to-br from-navy-800/95 to-navy-950/95 rounded-xl p-8 border-2 border-gold/30 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gold">
                {getEventType(events.find((e) => e.id === formEventId)) === 'referral'
                  ? 'Arkadaş Davet Formu'
                  : 'Bilet Talep Formu'}
              </h2>
              <button
                onClick={() => {
                  setShowTicketForm(false);
                  setFormData({
                    sponsorId: '',
                    siteUsername: '',
                    depositAmount: '',
                    proofImage: null
                  });
                }}
                className="text-white/60 hover:text-white"
              >
                <FaTimes className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {(() => {
                const event = events.find(e => e.id === formEventId);
                if (!event) return null;
                
                return (
                  <>
                    {/* Etkinlik Başlığı */}
                    <div className="mb-4">
                      <h3 className="text-xl font-bold text-white mb-2">{event.title}</h3>
                    </div>

                    {/* Etkinlik Açıklaması */}
                    {event.description && (
                      <div className="mb-4">
                        <p className="text-white/80 text-sm md:text-base">{event.description}</p>
                      </div>
                    )}

                    {/* Kurallar */}
                    {event.rules && (
                      <div className="mb-4">
                        <div className="flex items-center gap-2 mb-2">
                          <FaMapMarkerAlt className="text-white/60" />
                          <h3 className="text-white font-semibold">KURALLAR</h3>
                        </div>
                        <div className="bg-navy-800/50 rounded-lg p-4 border border-gold/20">
                          {event.rules.split('\n').map((rule, index) => (
                            <div key={index} className="flex items-start gap-2 mb-2 text-white/80 text-sm">
                              <FaExclamationTriangle className="text-yellow-400 mt-0.5 flex-shrink-0" />
                              <span>{rule}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div>
                      {sponsors.length === 1 ? (
                        <div className="text-white/80 text-sm mb-2">
                          Sponsor: <span className="text-white font-semibold">{sponsors[0]?.name}</span>
                        </div>
                      ) : (
                        <>
                          <label className="block text-white/80 mb-2">{getEventType(event) === 'referral' ? 'Kayıt olduğu site *' : 'Sponsor Seçin *'}</label>
                          <select
                            value={formData.sponsorId}
                            onChange={(e) => handleSponsorChange(e.target.value)}
                            className="w-full px-4 py-3 bg-navy-700 border border-gold/30 rounded-lg text-white focus:outline-none focus:border-gold"
                            required
                          >
                            <option value="">Sponsor seçin</option>
                            {sponsors.map(sponsor => (
                              <option key={sponsor.id} value={sponsor.id}>
                                {sponsor.name} {sponsor.ticket_multiplier > 1 || sponsor.ticketMultiplier > 1 ? `(${(sponsor.ticket_multiplier || sponsor.ticketMultiplier)}x çarpan)` : ''}
                              </option>
                            ))}
                          </select>
                        </>
                      )}
                      {formData.sponsorId && selectedSponsorMultiplier > 1 && getEventType(event) !== 'referral' && (
                        <div className="mt-3 p-4 bg-gradient-to-r from-amber-900/30 to-navy-800/50 rounded-lg border border-gold/30">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-white font-semibold">Çarpanlı bilet</span>
                            <span className="text-gold font-bold text-lg">{selectedSponsorMultiplier}x</span>
                          </div>
                          <p className="text-white/70 text-sm">
                            Bu sponsor için bilet adedi, onay sonrası {selectedSponsorMultiplier}x çarpan ile hesaplanır. Talebiniz yönetici onayına düşer; onaylandıktan sonra geçerli olur.
                          </p>
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="block text-white/80 mb-2">
                        {getEventType(event) === 'referral'
                          ? 'Kayıt olan arkadaşın site kullanıcı adı *'
                          : 'Site Kullanıcı Adı *'}
                      </label>
                      <input
                        type="text"
                        value={formData.siteUsername}
                        onChange={(e) => setFormData({ ...formData, siteUsername: e.target.value })}
                        className="w-full px-4 py-3 bg-navy-700 border border-gold/30 rounded-lg text-white focus:outline-none focus:border-gold"
                        placeholder={
                          getEventType(event) === 'referral'
                            ? 'Arkadaşınızın sitedeki kullanıcı adı'
                            : 'Site kullanıcı adınızı girin'
                        }
                        required
                      />
                    </div>

                    {getEventType(event) !== 'referral' && (
                    <>
                    <div>
                      <label className="block text-white/80 mb-2">Yatırım Tutarı (TL) *</label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.depositAmount}
                        onChange={(e) => setFormData({ ...formData, depositAmount: e.target.value })}
                        className="w-full px-4 py-3 bg-navy-700 border border-gold/30 rounded-lg text-white focus:outline-none focus:border-gold"
                        placeholder="Yatırım tutarınızı girin"
                        required
                        min={event.ticketPrice || event.ticket_price}
                      />
                      <p className="text-white/60 text-sm mt-1">
                        Her {event.ticketPrice || event.ticket_price}₺ yatırım için 1 bilet alabilirsiniz
                      </p>
                    </div>

                    <div>
                      <label className="block text-white/80 mb-2">Kanıt Görseli *</label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => setFormData({ ...formData, proofImage: e.target.files[0] })}
                        className="w-full px-4 py-3 bg-navy-700 border border-gold/30 rounded-lg text-white focus:outline-none focus:border-gold"
                        required
                      />
                    </div>
                    </>
                    )}

                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-gradient-to-r from-green-500 to-blue-500 hover:from-green-600 hover:to-blue-600 text-white font-bold py-4 rounded-lg transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <FaTicketAlt />
                      <span>{loading ? 'Gönderiliyor...' : getEventType(event) === 'referral' ? 'Davet Gönder' : 'Bilet Talep Et'}</span>
                    </button>
                  </>
                );
              })()}
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketsPage;
